"use client";

import React, { useEffect, useRef } from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import { cn } from "@/lib/utils";
import { useDictionary } from "@/lib/useDictionary";

interface ProcessInfographicProps {
  className?: string;
}

export default function ProcessInfographic({
  className,
}: ProcessInfographicProps) {
  const dict = useDictionary();

  const steps = [
    {
      title: dict.process.step1Title,
      hours: 1,
      accent: "from-sky-300 to-cyan-400",
    },
    {
      title: dict.process.step2Title,
      hours: 3,
      accent: "from-sky-400 via-blue-500 to-blue-600",
    },
    {
      title: dict.process.step3Title,
      hours: 6,
      accent: "from-blue-500 via-indigo-500 to-blue-700",
    },
    {
      title: dict.process.step4Title,
      hours: 4,
      accent: "from-blue-700 via-blue-800 to-slate-900",
    },
  ];

  const totalHours = steps.reduce((sum, item) => sum + item.hours, 0);

  const unitFor = (hours: number) =>
    hours === 1 ? dict.process.unitSingular : dict.process.headingUnit;

  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const count = useMotionValue(0);
  const roundedCount = useTransform(count, (latest) => Math.round(latest));

  useEffect(() => {
    if (isInView) {
      animate(count, totalHours, {
        duration: 2.2,
        ease: "easeOut",
        delay: 0.15,
      });
    }
  }, [isInView, count, totalHours]);

  return (
    <section
      ref={containerRef}
      className={cn(
        "relative overflow-hidden bg-section-light py-20",
        className,
      )}
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mx-auto mb-14 flex max-w-3xl flex-wrap items-baseline justify-center gap-x-3 gap-y-2 text-[var(--blue-900)] sm:mb-16"
        >
          <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">
            {dict.process.heading}
          </span>
          <span className="hero-ink text-6xl font-black sm:text-7xl">
            <motion.span>{roundedCount}</motion.span>+
          </span>
          <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">
            {dict.process.headingUnit}
          </span>
        </motion.h2>

        <ol className="mx-auto flex w-fit max-w-full flex-col md:grid md:w-auto md:grid-cols-4">
          {steps.map((item, index) => {
            const isFirst = index === 0;
            const isLast = index === steps.length - 1;

            return (
              <li
                key={`step-${index}`}
                className="relative flex min-h-[3.875rem] items-center gap-5 pb-6 last:pb-0 md:min-h-0 md:flex-col md:gap-0 md:px-3 md:pb-0"
              >
                {/* 手機：直軌，接到下一個節點的圓心 */}
                {!isLast && (
                  <motion.span
                    initial={{ scaleY: 0 }}
                    animate={isInView ? { scaleY: 1 } : {}}
                    transition={{
                      duration: 0.4,
                      delay: 0.45 + index * 0.12,
                      ease: "easeOut",
                    }}
                    className="absolute bottom-[-1.9375rem] left-[1.9375rem] top-[1.9375rem] w-0.5 origin-top rounded-full bg-[#c3d9f4] md:hidden"
                  />
                )}

                {/* 桌機：橫線，起於第一個圓、止於最後一個圓 */}
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={isInView ? { scaleX: 1 } : {}}
                  transition={{
                    duration: 0.45,
                    delay: 0.45 + index * 0.12,
                    ease: "easeOut",
                  }}
                  className={cn(
                    "absolute top-10 hidden h-0.5 origin-left rounded-full bg-[#c3d9f4] md:block",
                    isFirst && "left-1/2 right-0",
                    isLast && "left-0 right-1/2",
                    !isFirst && !isLast && "inset-x-0",
                  )}
                />

                <motion.div
                  initial={{ opacity: 0, scale: 0.7 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{
                    duration: 0.4,
                    delay: 0.55 + index * 0.12,
                    ease: [0.34, 1.4, 0.64, 1],
                  }}
                  className={cn(
                    "relative flex h-[3.875rem] w-[3.875rem] shrink-0 flex-col items-center justify-center rounded-full bg-gradient-to-br text-white shadow-[0_0_0_5px_rgba(255,255,255,0.88),0_8px_20px_rgba(30,64,175,0.22)] md:h-20 md:w-20 md:shadow-[0_0_0_7px_rgba(255,255,255,0.86),0_10px_24px_rgba(30,64,175,0.24)]",
                    item.accent,
                  )}
                >
                  <span className="text-[1.375rem] font-black leading-none md:text-[1.75rem]">
                    {item.hours}
                  </span>
                  <span className="mt-1 text-[0.6875rem] font-bold leading-none opacity-90 md:text-xs">
                    {unitFor(item.hours)}
                  </span>
                </motion.div>

                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.68 + index * 0.12 }}
                  className="text-[0.9375rem] font-bold leading-6 text-[var(--blue-900)] md:mt-5 md:text-center md:text-base"
                >
                  {item.title}
                </motion.p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
